// TwinViewer - 3D model viewer component using Twin Engine
// Uses an iframe to isolate the viewer's React from JustSell's React
import { useEffect, useRef, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '../hooks/useTranslation'
import type { TranslationKey } from '../../locales/types'
import {
  CubeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ExclamationCircleIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline'
import { twinApi, type TwinProject, type TwinConversionStatus } from '../lib/api'

// Twin Viewer global API types
interface TwinPartInfo {
  id: string
  name: string
  type?: string
  nodePath?: string
  bomData?: {
    partNumber: string
    description?: string
    quantity: number
    unit?: string
  }
}

interface TwinViewerProps {
  projectId: string
  onPartClick?: (partId: string, partName: string, bomData?: TwinPartInfo['bomData']) => void
  className?: string
}

type ViewerState = 'loading' | 'loading-viewer' | 'processing' | 'ready' | 'error' | 'no-model' | 'unavailable' | 'conversion-failed'

type ErrorType = 'network' | 'unavailable' | 'not-found' | 'conversion' | 'viewer' | 'unknown'

interface ViewerError {
  type: ErrorType
  message: string
  details?: string
  retryable: boolean
}

// Error type to translation key mappings
const ERROR_KEYS: Record<ErrorType, { title: TranslationKey; description: TranslationKey }> = {
  network: {
    title: 'twin.viewer.networkError',
    description: 'twin.viewer.networkErrorDesc',
  },
  unavailable: {
    title: 'twin.viewer.serviceUnavailable',
    description: 'twin.viewer.serviceUnavailableDesc',
  },
  'not-found': {
    title: 'twin.viewer.modelNotFound',
    description: 'twin.viewer.modelNotFoundDesc',
  },
  conversion: {
    title: 'twin.viewer.conversionFailed',
    description: 'twin.viewer.conversionFailedDesc',
  },
  viewer: {
    title: 'twin.viewer.viewerError',
    description: 'twin.viewer.viewerErrorDesc',
  },
  unknown: {
    title: 'twin.viewer.unknownError',
    description: 'twin.viewer.unknownErrorDesc',
  },
}

// Helper to classify errors
function classifyError(error: unknown, context: 'fetch' | 'viewer'): ViewerError {
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('Failed to fetch')) {
      return { type: 'network', message: error.message, retryable: true }
    }
    // Service unavailable
    if (error.message.includes('503') || error.message.includes('502') || error.message.includes('unavailable')) {
      return { type: 'unavailable', message: error.message, retryable: true }
    }
    // Not found
    if (error.message.includes('404') || error.message.includes('not found')) {
      return { type: 'not-found', message: error.message, retryable: false }
    }
    // Conversion errors
    if (error.message.includes('conversion') || error.message.includes('convert')) {
      return { type: 'conversion', message: error.message, retryable: false }
    }
    // Viewer-specific errors
    if (context === 'viewer') {
      return { type: 'viewer', message: error.message, details: error.stack, retryable: true }
    }
  }
  return { type: 'unknown', message: String(error), retryable: true }
}

export function TwinViewer({ projectId, onPartClick, className = '' }: TwinViewerProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [viewerState, setViewerState] = useState<ViewerState>('loading')
  const [viewerError, setViewerError] = useState<ViewerError | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [selectedPart, setSelectedPart] = useState<TwinPartInfo | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Fetch project info
  const { data: project, isLoading: projectLoading, error: projectError, refetch: refetchProject } = useQuery({
    queryKey: ['twin-project', projectId, retryCount],
    queryFn: () => twinApi.getProject(projectId),
    enabled: !!projectId,
    retry: 1,
  })

  // Fetch conversion status for processing projects
  const { data: conversionStatus } = useQuery({
    queryKey: ['twin-conversion-status', projectId],
    queryFn: () => twinApi.getConversionStatus(projectId),
    enabled: !!projectId && project?.status === 'processing',
    refetchInterval: project?.status === 'processing' ? 3000 : false,
  })

  // Retry handler
  const handleRetry = useCallback(() => {
    setViewerError(null)
    setRetryCount((c) => c + 1)
    setViewerState('loading')
  }, [])

  // Update viewer state based on project status
  useEffect(() => {
    if (projectLoading) {
      setViewerState('loading')
      return
    }

    if (projectError) {
      const error = classifyError(projectError, 'fetch')
      setViewerError(error)
      setViewerState(error.type === 'unavailable' ? 'unavailable' : 'error')
      return
    }

    if (!project) {
      setViewerState('no-model')
      return
    }

    switch (project.status) {
      case 'pending':
        setViewerState('no-model')
        break
      case 'processing':
        setViewerState('processing')
        break
      case 'ready':
        setViewerState('loading-viewer')
        break
      case 'failed':
        setViewerState('conversion-failed')
        setViewerError({
          type: 'conversion',
          message: 'CAD file conversion failed',
          details: 'The uploaded CAD file could not be converted to a viewable format.',
          retryable: false,
        })
        break
      default:
        setViewerState('no-model')
    }
  }, [project, projectLoading, projectError])

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only handle messages from our iframe
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return
      }

      const { type, part, message } = event.data || {}

      switch (type) {
        case 'ready':
          setViewerState('ready')
          break
        case 'partClick':
          if (part) {
            setSelectedPart(part)
            onPartClick?.(part.id, part.name, part.bomData)
          }
          break
        case 'partHover':
          // Could add hover state here if needed
          break
        case 'error':
          console.error('[TwinViewer] Viewer error:', message)
          setViewerError({ type: 'viewer', message, retryable: true })
          setViewerState('error')
          break
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [onPartClick])

  // Build iframe URL
  const iframeSrc = project?.status === 'ready'
    ? `/viewer.html#projectId=${projectId}&apiBase=/api/twin`
    : null

  // Control functions
  const highlightPart = useCallback((partId: string) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'highlightPart', partId }, '*')
  }, [])

  const zoomToPart = useCallback((partId: string) => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'zoomToPart', partId }, '*')
  }, [])

  const resetView = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'resetView' }, '*')
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true))
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false))
    }
  }, [])

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Expose methods for parent components
  useEffect(() => {
    const handleCommand = (event: CustomEvent<{ command: string; partId?: string }>) => {
      const { command, partId } = event.detail
      switch (command) {
        case 'highlight':
          if (partId) highlightPart(partId)
          break
        case 'zoom':
          if (partId) zoomToPart(partId)
          break
        case 'reset':
          resetView()
          break
      }
    }

    window.addEventListener('twinviewer:command', handleCommand as EventListener)
    return () => window.removeEventListener('twinviewer:command', handleCommand as EventListener)
  }, [highlightPart, zoomToPart, resetView])

  return (
    <div
      ref={containerRef}
      className={`relative bg-gray-900 rounded-lg overflow-hidden ${className}`}
      style={{ minHeight: '500px' }}
    >
      {/* Toolbar */}
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        {viewerState === 'ready' && (
          <button
            onClick={resetView}
            className="p-2 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
            title={t('twin.viewer.resetView')}
          >
            <ArrowPathIcon className="h-5 w-5 text-gray-600" />
          </button>
        )}
        <button
          onClick={toggleFullscreen}
          className="p-2 bg-white/90 rounded-lg shadow-sm hover:bg-white transition-colors"
          title={isFullscreen ? t('twin.viewer.exitFullscreen') : t('twin.viewer.fullscreen')}
        >
          {isFullscreen ? (
            <ArrowsPointingInIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ArrowsPointingOutIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* Iframe Viewer - shown when ready or loading viewer */}
      {iframeSrc && (viewerState === 'loading-viewer' || viewerState === 'ready') && (
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          className="absolute inset-0 w-full h-full border-0"
          style={{ minHeight: '500px' }}
          title="3D Model Viewer"
        />
      )}

      {/* Loading State */}
      {viewerState === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
          <ArrowPathIcon className="h-10 w-10 text-gray-400 animate-spin" />
          <p className="mt-4 text-sm text-gray-400">{t('twin.viewer.loading')}</p>
        </div>
      )}

      {/* Loading Viewer State */}
      {viewerState === 'loading-viewer' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 pointer-events-none">
          <CubeIcon className="h-12 w-12 text-brand-400 animate-pulse" />
          <p className="mt-4 text-sm text-gray-300">{t('twin.viewer.loadingViewer')}</p>
        </div>
      )}

      {/* Processing State */}
      {viewerState === 'processing' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
          <ArrowPathIcon className="h-10 w-10 text-brand-400 animate-spin" />
          <p className="mt-4 text-sm text-gray-300 font-medium">{t('twin.viewer.converting')}</p>
          {conversionStatus?.progress !== undefined && (
            <div className="mt-3 w-48">
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-500 transition-all duration-300"
                  style={{ width: `${conversionStatus.progress}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 text-center">
                {conversionStatus.progress}% {t('twin.viewer.complete')}
              </p>
            </div>
          )}
          {conversionStatus?.message && (
            <p className="mt-2 text-xs text-gray-500">{conversionStatus.message}</p>
          )}
        </div>
      )}

      {/* No Model State */}
      {viewerState === 'no-model' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
          <CubeIcon className="h-16 w-16 text-gray-300" />
          <p className="mt-4 text-sm text-gray-500">{t('twin.viewer.noModel')}</p>
          <p className="mt-1 text-xs text-gray-400">
            {t('twin.viewer.uploadPrompt')}
          </p>
        </div>
      )}

      {/* Service Unavailable State */}
      {viewerState === 'unavailable' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
          <SignalSlashIcon className="h-12 w-12 text-orange-400" />
          <p className="mt-4 text-sm text-gray-700 font-medium">
            {t(ERROR_KEYS.unavailable.title)}
          </p>
          <p className="mt-1 text-xs text-gray-500 text-center max-w-xs">
            {t(ERROR_KEYS.unavailable.description)}
          </p>
          <button
            onClick={handleRetry}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {t('twin.viewer.tryAgain')}
          </button>
        </div>
      )}

      {/* Conversion Failed State */}
      {viewerState === 'conversion-failed' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
          <ExclamationCircleIcon className="h-12 w-12 text-red-400" />
          <p className="mt-4 text-sm text-gray-700 font-medium">
            {t(ERROR_KEYS.conversion.title)}
          </p>
          <p className="mt-1 text-xs text-gray-500 text-center max-w-xs">
            {viewerError?.details || t(ERROR_KEYS.conversion.description)}
          </p>
          <p className="mt-3 text-xs text-gray-400">
            {t('twin.viewer.tryDifferentFormat')}
          </p>
        </div>
      )}

      {/* General Error State */}
      {viewerState === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400" />
          <p className="mt-4 text-sm text-gray-700 font-medium">
            {viewerError ? t(ERROR_KEYS[viewerError.type].title) : t('twin.viewer.loadFailed')}
          </p>
          <p className="mt-1 text-xs text-gray-500 text-center max-w-xs">
            {viewerError ? t(ERROR_KEYS[viewerError.type].description) : t('twin.viewer.errorOccurred')}
          </p>
          {viewerError?.details && (
            <details className="mt-2 max-w-xs">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                {t('twin.viewer.technicalDetails')}
              </summary>
              <p className="mt-1 text-xs text-gray-400 font-mono bg-gray-200 p-2 rounded">
                {viewerError.details}
              </p>
            </details>
          )}
          <div className="mt-4 flex gap-2">
            {viewerError?.retryable && (
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t('twin.viewer.tryAgain')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Part Info Overlay */}
      {selectedPart && viewerState === 'ready' && (
        <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{selectedPart.name}</p>
              {selectedPart.bomData && (
                <div className="mt-1 space-y-0.5">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">{t('twin.viewer.partNumber')}</span> {selectedPart.bomData.partNumber}
                  </p>
                  {selectedPart.bomData.description && (
                    <p className="text-xs text-gray-500">{selectedPart.bomData.description}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">{t('twin.viewer.quantity')}</span> {selectedPart.bomData.quantity}
                    {selectedPart.bomData.unit && ` ${selectedPart.bomData.unit}`}
                  </p>
                </div>
              )}
              {!selectedPart.bomData && (
                <p className="text-xs text-gray-500 mt-1">{t('twin.viewer.partId')}: {selectedPart.id}</p>
              )}
            </div>
            <button
              onClick={() => setSelectedPart(null)}
              className="ml-4 text-gray-400 hover:text-gray-600 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Conversion status badge component
export function TwinStatusBadge({ status }: { status: TwinProject['status'] }) {
  const { t } = useTranslation()
  const config: Record<string, { labelKey: TranslationKey; className: string }> = {
    draft: { labelKey: 'twin.status.draft', className: 'bg-blue-100 text-blue-600' },
    pending: { labelKey: 'twin.status.pending', className: 'bg-gray-100 text-gray-600' },
    processing: { labelKey: 'twin.status.processing', className: 'bg-yellow-100 text-yellow-700' },
    ready: { labelKey: 'twin.status.ready', className: 'bg-green-100 text-green-700' },
    failed: { labelKey: 'twin.status.failed', className: 'bg-red-100 text-red-700' },
  }

  const { labelKey, className } = config[status] || config.pending

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {t(labelKey)}
    </span>
  )
}

// Export viewer control functions for external use
export const TwinViewerControls = {
  highlightPart: (partId: string) => {
    window.dispatchEvent(
      new CustomEvent('twinviewer:command', { detail: { command: 'highlight', partId } })
    )
  },
  zoomToPart: (partId: string) => {
    window.dispatchEvent(
      new CustomEvent('twinviewer:command', { detail: { command: 'zoom', partId } })
    )
  },
  resetView: () => {
    window.dispatchEvent(
      new CustomEvent('twinviewer:command', { detail: { command: 'reset' } })
    )
  },
}
